import api from './axios';
import { AxiosError } from 'axios';

// Backend response wrapper interface
interface BackendResponse<T> {
  data: T;
  message: string;
  success: boolean;
  timestamp: string;
}

export interface Farm {
  id: string;
  name: string;
  location: string;
  size: number;
  status: 'active' | 'inactive' | 'maintenance';
  description: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFarmDto {
  name: string;
  location: string;
  size: number;
  status: 'active' | 'inactive' | 'maintenance';
  description: string;
  tenantId?: string;
}

export interface UpdateFarmDto extends Partial<CreateFarmDto> {}

// Type guard to check if an object is a Farm
function isFarm(obj: any): obj is Farm {
  return obj && typeof obj === 'object' && 'id' in obj && 'name' in obj && 'location' in obj;
}

export const fetchFarms = async (): Promise<Farm[]> => {
  try {
    const response = await api.get<BackendResponse<Farm[]>>('/farms');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    // Fallback: check if the response is directly an array
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    console.error('Invalid farms response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching farms:', error);
    throw error;
  }
};

export const fetchFarm = async (id: string): Promise<Farm> => {
  try {
    const response = await api.get<BackendResponse<Farm>>(`/farms/${id}`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Farm object
    if (isFarm(responseData)) {
      return responseData;
    }
    
    console.error('Invalid farm response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error fetching farm ${id}:`, error);
    throw error;
  }
};

export const createFarm = async (data: CreateFarmDto): Promise<Farm> => {
  try {
    const response = await api.post<BackendResponse<Farm>>('/farms', data);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Farm object
    if (isFarm(responseData)) {
      return responseData;
    }
    
    console.error('Invalid create farm response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error creating farm:', error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const updateFarm = async (id: string, data: UpdateFarmDto): Promise<Farm> => {
  try {
    const response = await api.patch<BackendResponse<Farm>>(`/farms/${id}`, data);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Farm object
    if (isFarm(responseData)) {
      return responseData;
    }
    
    console.error('Invalid update farm response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error updating farm ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const deleteFarm = async (id: string): Promise<void> => {
  try {
    await api.delete(`/farms/${id}`);
  } catch (error) {
    console.error(`Error deleting farm ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};
